import { Injectable } from '@angular/core';
import { createClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import Fernet from 'fernet-web';
import { describePeriod, LicensePeriod, todayIso } from './license-timeline';

/** `company.id` of this client in the Imaxine license project (TATA 1 mg) */
export const LICENSE_COMPANY_ID = 3;

@Injectable({
    providedIn: 'root'
})
export class LicenseService {
    private supabase: any;
    public licenseData: any = null;
    private lastError: string | null = null;

    constructor() {
        this.supabase = createClient(environment.SUPABASE_URL_IMAXINE, environment.SUPABASE_KEY_IMAXINE);
    }

    // --- Helpers for Fernet (browser-compatible using Web Crypto) ---
    private base64UrlToBytes(input: string): Uint8Array {
        input = input.replace(/-/g, '+').replace(/_/g, '/');
        while (input.length % 4) input += '=';
        const str = atob(input);
        const bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i);
        return bytes;
    }

    private bytesToUtf8(bytes: Uint8Array): string {
        const decoder = new TextDecoder();
        return decoder.decode(bytes);
    }

    private async importHmacKey(keyBytes: Uint8Array) {
        return crypto.subtle.importKey(
            'raw',
            keyBytes.buffer as ArrayBuffer,
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign', 'verify']
        );
    }

    private async importAesKey(keyBytes: Uint8Array) {
        return crypto.subtle.importKey(
            'raw',
            keyBytes.buffer as ArrayBuffer,
            { name: 'AES-CBC' },
            false,
            ['decrypt']
        );
    }

    private equalBytes(a: Uint8Array, b: Uint8Array) {
        if (a.length !== b.length) return false;
        let diff = 0;
        for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
        return diff === 0;
    }

    private async fernetDecrypt(token: string, keyB64Url: string): Promise<string> {
        this.ensureWebCryptoAvailable();

        const fernet = await Fernet.create(keyB64Url);
        return await fernet.decrypt(token);
    }

    private ensureWebCryptoAvailable(): void {
        if (globalThis.crypto?.subtle) return;

        const protocol = globalThis.location?.protocol || 'unknown protocol';
        throw new Error(
            'Web Crypto is not available on this origin (' + protocol + '). Serve the production app over HTTPS because license decryption requires crypto.subtle.'
        );
    }

    // --- Supabase fetches ---
    async fetchEncryptionKey(): Promise<string | null> {
        try {
            // const { data, error } = await this.supabase.from('encryption').select('encrypt_key').single();
            // if (error) return null;
            // return data?.encrypt_key || null;

            const data = environment.ENCRYPTION_KEY || null;
            return data;

        } catch (e) {
            return null;
        }
    }

    async fetchActiveOrLastLicense(): Promise<{ token: string; status: 'active' | 'expired' } | null> {
        try {
            const currentDate = todayIso(); // local YYYY-MM-DD (a UTC date lags IST until 05:30)
            const { data: activeData, error: activeError } = await this.supabase
                .from('license')
                .select('license, valid_from, valid_to, created_at')
                .eq('company_id', LICENSE_COMPANY_ID)
                .lte('valid_from', currentDate)
                .gte('valid_to', currentDate)
                .limit(1);

            if (activeError) {
                console.error('Supabase active license fetch error', activeError);
            }

            if (activeData && activeData.length > 0) {
                return { token: activeData[0].license, status: 'active' };
            }

            const { data: lastData, error: lastError } = await this.supabase
                .from('license')
                .select('license, valid_from, valid_to, created_at')
                .eq('company_id', LICENSE_COMPANY_ID)
                .order('valid_to', { ascending: false })
                .order('created_at', { ascending: false })
                .limit(1);

            if (lastError) {
                console.error('Supabase last license fetch error', lastError);
            }

            if (lastData && lastData.length > 0) {
                return { token: lastData[0].license, status: 'expired' };
            }

            return null;
        } catch (e) {
            console.error('Unexpected license fetch error', e);
            return null;
        }
    }

    /**
     * Every license period of this client, newest first. comment_for_client is
     * only fetched when asked for (the admin license list), never for the banner.
     */
    async fetchCompanyLicensePeriods(withClientComment = false): Promise<LicensePeriod[]> {
        const { data, error } = await this.supabase
            .from('license')
            .select(withClientComment ? 'id, valid_from, valid_to, comment_for_client' : 'id, valid_from, valid_to')
            .eq('company_id', LICENSE_COMPANY_ID)
            .order('valid_from', { ascending: false });

        if (error) throw new Error(error.message || 'Could not load licenses');
        return data ?? [];
    }

    async loadLicense(): Promise<any> {
        this.lastError = null;
        const result = await this.fetchActiveOrLastLicense();
        if (!result) throw new Error('License not found');

        const encrypted = result.token;
        const key = await this.fetchEncryptionKey();
        if (!key) throw new Error('Encryption key not found');


        try {
            const decoded = await this.fernetDecode(encrypted, key);
            this.licenseData = JSON.parse(decoded);
            this.licenseData.license_status = result.status;
            // console.log('License loaded successfully:', this.licenseData);
            return this.licenseData;
        } catch (e: any) {
            console.error('License decryption failed:', e);
            this.lastError = 'Failed to decrypt license: ' + (e?.message || e);
            throw new Error(this.lastError);
        }
    }

    private async fernetDecode(token: string, key: string): Promise<string> {

        const data = await this.fernetDecrypt(token, key);

        return data;
    }

    async isValid(): Promise<boolean> {
        // Dev escape hatch, e.g. when served over plain http where crypto.subtle
        // (needed to decrypt the licence) is unavailable. Never honoured in a
        // production build.
        if (!environment.production && (environment as { SKIP_LICENSE_CHECK?: boolean }).SKIP_LICENSE_CHECK) {
            console.warn('Licence check skipped: SKIP_LICENSE_CHECK is set in a non-production environment.');
            return true;
        }

        // console.log('Checking license validity...');
        // Re-fetch when nothing is loaded yet or the loaded license no longer
        // covers today (tab left open past expiry, or a renewal was issued), so
        // the user never needs a hard refresh to pick up the new license.
        if (!this.isActive(this.licenseData)) {
            // console.log('License not loaded or not active, loading from server...');
            try {
                await this.loadLicense();
            } catch (e) {
                return false;
            }
        }
        // console.log('License data:', this.licenseData);
        return this.isActive(this.licenseData);
    }

    /**
     * Whole calendar days, both ends inclusive: the license works all day on
     * its end date. (new Date('YYYY-MM-DD') is midnight UTC, i.e. 05:30 IST,
     * which used to lock users out on the last morning.)
     */
    private isActive(license: any): boolean {
        const start = license?.start_date || license?.valid_from;
        const end = license?.end_date || license?.valid_to;
        if (!start || !end) return false;
        return describePeriod({ id: 0, valid_from: start, valid_to: end }).state === 'active';
    }

    async getInfo(): Promise<any> {
        if (!this.licenseData) await this.loadLicense();
        return this.licenseData;
    }

    getLastError(): string | null {
        return this.lastError;
    }
}
// update data 